<template>
  <div class="church-services-page">
    <v-container>
      <!-- Page Title -->
      <v-row class="mb-6">
        <v-col cols="12">
          <h1 class="text-h3 font-weight-bold text-center text-accent mb-2">Kerkdiensten</h1>
          <div class="text-center">
            <v-divider class="mb-4 mx-auto" width="100" thickness="3" color="primary"></v-divider>
          </div>
          <p class="text-center text-body-1 mx-auto" style="max-width: 800px">
            Hier vindt u informatie over onze kerkdiensten, livestream, sprekers en andere
            activiteiten in NGK Eemdijk.
          </p>
        </v-col>
      </v-row>

      <!-- Main Content Grid -->
      <v-row>
        <!-- Services Times Card -->
        <v-col cols="12" md="6" class="mb-6">
          <v-card elevation="2" height="100%">
            <v-img
              src="/images/kerkBinnen.jpeg"
              height="200"
              cover
              class="rounded-t-lg"
              gradient="to bottom, rgba(0,0,0,.1), rgba(0,0,0,.5)"
              alt="Kerkdienst NGK Eemdijk"
            >
              <div class="fill-height d-flex align-end">
                <v-card-title class="text-white text-h5">Diensten</v-card-title>
              </div>
            </v-img>
            <v-card-text>
              <div class="d-flex align-center mb-4">
                <v-icon color="primary" size="large" class="me-3">mdi-calendar-clock</v-icon>
                <div>
                  <div class="text-h6 mb-1">Reguliere diensten</div>
                  <div class="text-body-1">{{ regularServiceTimesText }}</div>
                </div>
              </div>

              <v-divider class="my-4"></v-divider>

              <div class="d-flex align-center mb-4">
                <v-icon color="primary" size="large" class="me-3">mdi-church</v-icon>
                <div>
                  <div class="text-h6 mb-1">Locatie</div>
                  <div class="text-body-1">
                    Westerkerk<br />
                    Eemdijk 63<br />
                    3754 ND Eemdijk
                  </div>
                </div>
              </div>

              <v-divider class="my-4"></v-divider>

              <div class="d-flex align-center">
                <v-icon color="primary" size="large" class="me-3">mdi-information</v-icon>
                <div>
                  <div class="text-h6 mb-1">Bijzondere diensten</div>
                  <div class="text-body-1">
                    Voor feestdagen en bijzondere diensten, zie ons rooster hieronder.
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Livestream Card -->
        <v-col cols="12" md="6" class="mb-6">
          <v-card elevation="2" height="100%">
            <v-img
              src="/images/polderEemdijk.jpeg"
              height="200"
              cover
              class="rounded-t-lg"
              gradient="to bottom, rgba(0,0,0,.1), rgba(0,0,0,.5)"
              alt="Kerkdienst livestream"
            >
              <div class="fill-height d-flex align-end">
                <v-card-title class="text-white text-h5">Livestream</v-card-title>
              </div>
            </v-img>
            <v-card-text>
              <div class="d-flex align-center mb-4">
                <v-icon color="primary" size="large" class="me-3">mdi-video</v-icon>
                <div>
                  <div class="text-h6 mb-1">Online meekijken</div>
                  <div class="text-body-1">
                    Alle diensten worden live uitgezonden via onze livestream.
                  </div>
                </div>
              </div>

              <v-divider class="my-4"></v-divider>

              <div class="d-flex align-center mb-4">
                <v-icon color="primary" size="large" class="me-3">mdi-youtube</v-icon>
                <div>
                  <div class="text-h6 mb-1">YouTube kanaal</div>
                  <div class="text-body-1">
                    Bekijk onze diensten live of later terug via ons YouTube kanaal.
                  </div>
                  <v-btn
                    color="error"
                    class="mt-2"
                    prepend-icon="mdi-youtube"
                    variant="tonal"
                    href="https://www.youtube.com/@gkveemdijk2925/streams"
                    target="_blank"
                  >
                    Naar YouTube kanaal
                  </v-btn>
                </div>
              </div>

              <v-divider class="my-4"></v-divider>

              <div class="d-flex align-center">
                <v-icon color="primary" size="large" class="me-3">mdi-phone</v-icon>
                <div>
                  <div class="text-h6 mb-1">Online dienst techniek</div>
                  <div class="text-body-1">
                    Voor vragen over livestream kunt u contact opnemen met Rene Koops 06-40654091.
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Services Schedule -->
      <v-row class="mb-8">
        <v-col cols="12">
          <h2 class="text-h4 text-accent mb-4">Preekrooster</h2>
          <v-card elevation="2">
            <v-card-text>
              <div
                class="d-flex flex-column flex-md-row align-md-center justify-space-between ga-3 mb-4"
              >
                <div>
                  <div class="text-subtitle-1 font-weight-medium">{{ visibleRangeLabel }}</div>
                </div>

                <div class="d-flex flex-wrap ga-2">
                  <v-btn
                    variant="tonal"
                    color="primary"
                    prepend-icon="mdi-chevron-left"
                    :disabled="windowOffsetWeeks === 0"
                    @click="showPreviousWindow"
                  >
                    Eerder
                  </v-btn>
                  <v-btn
                    variant="tonal"
                    color="primary"
                    prepend-icon="mdi-calendar-today"
                    :disabled="windowOffsetWeeks === 0"
                    @click="resetWindow"
                  >
                    Vandaag
                  </v-btn>
                  <v-btn
                    variant="tonal"
                    color="primary"
                    append-icon="mdi-chevron-right"
                    @click="showNextWindow"
                  >
                    Later
                  </v-btn>
                </div>
              </div>

              <v-alert
                v-if="roosterError"
                type="error"
                variant="tonal"
                class="mb-4"
                icon="mdi-alert-circle"
              >
                {{ roosterError }}
              </v-alert>

              <v-progress-linear
                v-if="isLoadingRooster"
                indeterminate
                color="primary"
                class="mb-4"
              />

              <v-table v-if="visibleServices.length > 0">
                <thead>
                  <tr>
                    <th>Datum</th>
                    <th>Tijd</th>
                    <th>Voorganger</th>
                    <th>Bijzonderheden</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(service, index) in visibleServices" :key="index">
                    <td>{{ service.date }}</td>
                    <td>{{ service.time }}</td>
                    <td>{{ service.preacher }}</td>
                    <td>{{ service.notes }}</td>
                  </tr>
                </tbody>
              </v-table>

              <v-alert
                v-else-if="!isLoadingRooster"
                type="info"
                variant="tonal"
                icon="mdi-calendar-blank"
              >
                Er zijn geen diensten in deze periode.
              </v-alert>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Church Activities -->
      <v-row>
        <v-col cols="12">
          <h2 class="text-h4 text-accent mb-4">Kerkelijke activiteiten</h2>
          <v-alert color="highlight" variant="tonal" icon="mdi-information" class="mb-4">
            Naast de reguliere kerkdiensten bieden we diverse activiteiten aan voor alle
            leeftijdsgroepen.
          </v-alert>
        </v-col>

        <!-- Activity Cards -->
        <v-col
          v-for="(activity, index) in churchActivities"
          :key="index"
          cols="12"
          md="4"
          sm="6"
          class="mb-4"
        >
          <v-card elevation="2" height="100%">
            <v-card-title class="d-flex align-center">
              <v-icon :color="activity.iconColor" size="large" class="me-2">{{
                activity.icon
              }}</v-icon>
              {{ activity.title }}
            </v-card-title>
            <v-card-text>
              <p>{{ activity.description }}</p>
              <div class="d-flex align-center mt-4">
                <v-icon size="small" color="highlight" class="me-2">mdi-calendar</v-icon>
                <span>{{ activity.schedule }}</span>
              </div>
              <div v-if="activity.location" class="d-flex align-center mt-2">
                <v-icon size="small" color="highlight" class="me-2">mdi-map-marker</v-icon>
                <span>{{ activity.location }}</span>
              </div>
              <div v-if="activity.contact" class="d-flex align-center mt-2">
                <v-icon size="small" color="highlight" class="me-2">mdi-account</v-icon>
                <span>{{ activity.contact }}</span>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { getSundayServiceText } from '../../shared/serviceTimes';

type UpcomingService = {
  isoDate: string;
  time: string;
  preacher: string;
  notes: string;
};

const isLoadingRooster = ref(false);
const roosterError = ref<string | null>(null);
const regularServiceTimesText = getSundayServiceText();
const windowOffsetWeeks = ref(0);

function getTodayIsoDate(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Amsterdam',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(new Date());
}

function addDaysToIsoDate(isoDate: string, days: number): string {
  const date = new Date(`${isoDate}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

function getIsoDayOfWeek(isoDate: string): number {
  const date = new Date(`${isoDate}T00:00:00Z`);
  const day = date.getUTCDay();
  return day === 0 ? 7 : day;
}

const baseStartDate = getTodayIsoDate();
const daysUntilEndOfNextWeek = 14 - getIsoDayOfWeek(baseStartDate);

const windowStartDate = computed(() =>
  addDaysToIsoDate(baseStartDate, windowOffsetWeeks.value * 7)
);
const windowEndDate = computed(() => {
  if (windowOffsetWeeks.value === 0) {
    return addDaysToIsoDate(windowStartDate.value, daysUntilEndOfNextWeek);
  }

  return addDaysToIsoDate(windowStartDate.value, 13);
});

const visibleRangeLabel = computed(() => {
  return `${formatDateNl(windowStartDate.value)} t/m ${formatDateNl(windowEndDate.value)}`;
});

function formatDateNl(isoDate: string): string {
  const d = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(d.getTime())) return isoDate;
  return new Intl.DateTimeFormat('nl-NL', {
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  }).format(d);
}

const visibleServices = computed(() => {
  return upcomingServices.value
    .filter(
      (service) =>
        service.isoDate >= windowStartDate.value && service.isoDate <= windowEndDate.value
    )
    .map((service) => ({
      date: formatDateNl(service.isoDate),
      time: service.time,
      preacher: service.preacher,
      notes: service.notes
    }));
});

function showPreviousWindow() {
  windowOffsetWeeks.value = Math.max(0, windowOffsetWeeks.value - 1);
}

function showNextWindow() {
  windowOffsetWeeks.value += 1;
}

function resetWindow() {
  windowOffsetWeeks.value = 0;
}

// Fallback sample data
const upcomingServices = ref<UpcomingService[]>([
  {
    isoDate: '2026-07-26',
    time: '9:30',
    preacher: 'Ds. A.M. Clement',
    notes: ''
  },
  {
    isoDate: '2026-07-26',
    time: '16:30',
    preacher: 'Ds. P. van Veelen',
    notes: ''
  },
  {
    isoDate: '2026-08-02',
    time: '9:30',
    preacher: 'Ds. A.M. Clement',
    notes: 'Heilig Avondmaal'
  },
  {
    isoDate: '2026-08-02',
    time: '16:30',
    preacher: 'Ds. J. de Wolf',
    notes: ''
  },
  {
    isoDate: '2026-08-09',
    time: '9:30',
    preacher: 'Ds. A.M. Clement',
    notes: ''
  },
  {
    isoDate: '2026-08-09',
    time: '16:30',
    preacher: 'Ds. B. van Zuijlekom',
    notes: ''
  }
]);

onMounted(async () => {
  isLoadingRooster.value = true;
  roosterError.value = null;

  try {
    const res = await fetch('/api/preekrooster', { cache: 'no-store' });
    if (!res.ok) {
      throw new Error(`Kon preekrooster niet ophalen (${res.status}).`);
    }

    const items = (await res.json()) as Array<{
      date: string;
      time: string;
      preacher: string;
      notes: string;
    }>;
    upcomingServices.value = items.map((i) => ({
      isoDate: i.date,
      time: i.time,
      preacher: i.preacher,
      notes: i.notes ?? ''
    }));
  } catch (e) {
    roosterError.value = e instanceof Error ? e.message : 'Kon preekrooster niet ophalen.';
  } finally {
    isLoadingRooster.value = false;
  }
});

// Sample data for church activities
const churchActivities = [
  {
    title: 'Miniwijk',
    icon: 'mdi-book-open-variant',
    iconColor: 'accent',
    description:
      'Bijbelstudiegroep waar we samen een bijbelboek bestuderen naar aanleiding van een het jaarthema en het thema bespreken.',
    schedule: 'Zondag',
    location: 'Consistorie kerk',
    contact: 'Ds. A.M. Clement'
  },
  {
    title: 'Kindernevendienst',
    icon: 'mdi-school',
    iconColor: 'accent',
    description: 'Tijdens de ochtenddienst voor kinderen van 4-12 jaar.',
    schedule: 'Zondag, tijdens ochtenddienst',
    location: 'Zaaltje achter kerk',
    contact: 'Gabriëlle van Twillert'
  },
  {
    title: 'Gemeenteavond',
    icon: 'mdi-home-group',
    iconColor: 'accent',
    description: 'Informatieavond over actuele zaken binnen de gemeente.',
    schedule: 'een keer per jaar',
    location: 'Kerkzaal',
    contact: 'Kerkenraad'
  }
];
</script>

<style scoped>
.church-services-page {
  padding-bottom: 2rem;
}
</style>
